// @flow
import React, { useState, useEffect } from "react";
import { Button, Responsive } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import * as classnames from "classnames";
import { withTranslation } from "react-i18next";
import { fetchPromotionCode } from "../../../redux/actions/couponCodes";
import { loadCustomTrackingData } from "Commons/Utilities/Tracking/TrackingHandler";

import { copyToClipboard, TextSegment } from "../CouponCodeBox/CouponCodeBox";

import "./PromotionBanner.scss";
import { setShowPromotionBanner } from "../../../redux/actions/ui";
import GoogleTagManager from "../../Utilities/GoogleTagManager";

type BannerProps = {|
  title: string,
  image: string,
  code: string,
  couponCodeDescription?: string,
  onBtnClick: () => void,
  t: typeof TFunction,
|};

export const PromotionBottomBanner = ({ title, image, code, couponCodeDescription, onBtnClick, t }: BannerProps) => {
  const [showImage, setShowImage] = useState(false);

  return (
    <div className={classnames("footer-card", showImage ? "open" : "closed")}>
      <div className={"promotion-main-container"} onClick={() => setShowImage(!showImage)}>
        <div className={"promotion-code-side-segment"} />
        <div className={"promotion-banner-content-wrapper"}>
          <div>
            <input readOnly className={"hidden-input"} id={"copyValueHolder"} value={code} />
          </div>
          <Responsive {...Responsive.onlyMobile}>
            <div className={"promotion-banner-text-wrapper"}>
              <TextSegment couponCodeTitle={title} couponCodeDescription={couponCodeDescription || ""} />
            </div>
            {showImage && (
              <div className={"promotion-banner-center-segment"}>
                <h3 className={"h3-w-right-margin"}> {t("promotionBanner.withCode")}</h3>
                <div>
                  <Button
                    className={"all-copy"}
                    circular
                    content={code}
                    icon={"copy"}
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard();
                    }}
                  />
                </div>
              </div>
            )}
          </Responsive>

          <Responsive minWidth={Responsive.onlyMobile.maxWidth}>
            <div className={"promotion-banner-text-wrapper"}>
              <TextSegment couponCodeTitle={title} couponCodeDescription={couponCodeDescription || ""} />
            </div>
            <div className={"promotion-banner-center-segment"}>
              <h3 className={"h3-w-right-margin"}> {t("promotionBanner.withCode")}</h3>
              <div>
                <Button
                  className={"all-copy"}
                  circular
                  content={code}
                  icon={"copy"}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard();
                  }}
                />
              </div>
            </div>
            <div className={"promotion-banner-image-wrapper"}>
              <img
                alt={"banner"}
                className={classnames("promotion-banner-image", showImage ? "shown" : "not-shown")}
                src={image}
              />
            </div>
          </Responsive>
        </div>
      </div>
      <Responsive {...Responsive.onlyMobile}>
        <div>
          <Button
            className={"promotion-banner-expand-btn"}
            circular
            icon={showImage ? "angle double down" : "angle double up"}
            onClick={() => setShowImage(!showImage)}
          />
        </div>
      </Responsive>
      <div className={"promotion-code-side-segment"}>
        <div>
          <Button className={"promotion-banner-close-btn"} circular icon={"close"} onClick={onBtnClick} />
        </div>
      </div>
    </div>
  );
};

const PromotionBanner = ({ t }: { t: typeof TFunction }) => {
  const dispatch = useDispatch();

  const show = useSelector(({ ui: { showPromotionBanner } }) => showPromotionBanner);

  useEffect(() => {
    if (show) {
      const promotionCode = loadCustomTrackingData("ppc_campaign_id");
      dispatch(fetchPromotionCode(promotionCode));
    }
  }, [dispatch, show]);

  const [couponCodeValue, setCouponCodeValue] = useState("");
  const showBannerValue = show;
  const onBtnClick = () => {
    GoogleTagManager.promotionBannerClosed();
    dispatch(setShowPromotionBanner(false));
  };

  const couponCode = useSelector(({ couponCodes: { couponCode } }) => couponCode);

  if (!couponCode) {
    return null;
  }

  if (couponCode.promotionCode !== couponCodeValue) {
    setCouponCodeValue(couponCode.promotionCode);
    return null;
  }

  if (showBannerValue === false) {
    return null;
  }
  return (
    <PromotionBottomBanner
      image={couponCode.imageUrl}
      title={couponCode.title}
      code={couponCodeValue.toUpperCase()}
      onBtnClick={onBtnClick}
      t={t}
    />
  );
};

export default withTranslation("common")(PromotionBanner);
